import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import SEO from '../components/SEO'
import InteractiveLibroGame from '../components/InteractiveLibroGame'

export default function BookPage() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <div className="min-h-screen pt-32 pb-24 px-4">
      <SEO 
        title="Il Libro &quot;Il Punto di Respawn&quot;"
        description="Scopri il manuale pratico per chi si è perso nei loop dopaminergici della vita e vuole sbloccare l'azione reale. Scritto da Amedeo Sbardellotto."
      />
      <div className="max-w-4xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-12">
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-nerd-white mb-4">
              Il{' '}
              <span className="text-gradient">Punto di Respawn.</span>
            </h1>
            <p className="text-nerd-gray text-base md:text-lg max-w-2xl mx-auto">
              Il primo libro-game evolutivo per hackerare la procrastinazione reale usando le meccaniche dei videogiochi.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-10 mb-16">
            {/* Copertina con punto di domanda cyberpunk */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div
                className="w-56 h-80 rounded-lg overflow-hidden relative group cursor-pointer"
                style={{
                  border: '1px solid rgba(139,92,246,0.45)',
                  boxShadow: '0 0 45px rgba(139,92,246,0.15), 0 0 90px rgba(0,212,255,0.08)',
                  background: '#050510',
                }}
              >
                {/* Tech lines background */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.12),transparent_70%)]" />
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-4 relative z-10">
                  <span className="font-display text-[90px] font-bold text-gradient leading-none mb-3 animate-pulse">?</span>
                  <span className="font-display text-sm font-bold text-nerd-white tracking-widest uppercase mb-1">Il Punto di Respawn</span>
                  <span className="text-[9px] text-nerd-cyan uppercase tracking-wider font-display font-semibold">Copertina Segreta</span>
                </div>
                <div className="absolute top-2 left-2 w-4 h-4 border-l border-t border-nerd-purple/40" />
                <div className="absolute top-2 right-2 w-4 h-4 border-r border-t border-nerd-purple/40" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-nerd-purple/40" />
                <div className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-nerd-purple/40" />
              </div>
            </div>

            {/* Narrativa e Copy del Libro */}
            <div className="flex-1 space-y-5">
              <div className="manga-panel rounded-xl p-6">
                <h2 className="font-display text-lg font-bold text-nerd-white mb-3">Cos'è questo manuale?</h2>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed mb-3">
                  <strong>Il Punto di Respawn</strong> non è un noioso saggio accademico né un freddo trattato motivazionale. È un <strong>libro-game evolutivo per la crescita personale</strong> scritto appositamente per chi ha passato migliaia di ore davanti a uno schermo ed è finito per perdersi nei complessi loop dopaminergici della vita reale.
                </p>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed">
                  Attraverso capitoli strutturati come <strong>Quest principali</strong>, tabelle di progressione, auto-valutazioni RPG e strategie pratiche basate sulle neuroscienze e sulla terapia cognitivo-comportamentale, questo libro ti guiderà a convertire le tue abilità da gamer (focus estremo, tenacia, pianificazione e tolleranza al fallimento) in risultati reali offline.
                </p>
              </div>

              <div className="manga-panel rounded-xl p-6">
                <h2 className="font-display text-lg font-bold text-nerd-white mb-3">Perché l'ho scritto così?</h2>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed italic mb-3">
                  "L'ho scritto perché ero esattamente come te. Ero un fuoriclasse nei mondi virtuali, sbloccavo traguardi epici online, ma la mia vita reale era bloccata al Livello 1, avvolta dalla nebbia della procrastinazione e della paura di fallire offline."
                </p>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed">
                  Ho capito che il problema non sono le nostre passioni né i videogiochi in sé, ma la mancanza di un <strong>sistema di feedback visibile</strong> nel mondo reale. Questo libro è nato per hackerare il tuo cervello, darti le regole concrete per superare la safe zone dopaminergica e segnare il tuo definitivo punto di respawn.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Lead Magnet Libro-Game */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold text-nerd-white mb-2">
              Pronto a testare la tua build?
            </h2>
            <p className="text-nerd-gray text-xs md:text-sm max-w-md mx-auto leading-relaxed">
              Affronta l'avventura a bivi narrativa qui sotto. Calcola le tue statistiche in tempo reale e sblocca gratis la tua Guida di Sopravvivenza personalizzata!
            </p>
          </div>
          <InteractiveLibroGame />
        </div>
      </div>
    </div>
  )
}
